import { useEffect } from 'react';

/**
 * Injects one or more JSON-LD schema blocks into <head> for the duration of the page mount.
 * Pass a single schema object or an array. Each block is wrapped in its own <script>.
 *
 * Usage:
 *   useJsonLd(productSchema(data));
 *   useJsonLd([productSchema(data), breadcrumbSchema(crumbs)]);
 */
export const useJsonLd = (schemas) => {
  useEffect(() => {
    const blocks = Array.isArray(schemas) ? schemas : [schemas];
    const nodes = blocks.filter(Boolean).map((schema) => {
      const tag = document.createElement('script');
      tag.type = 'application/ld+json';
      tag.dataset.jsonldDynamic = '1';
      tag.text = JSON.stringify(schema);
      document.head.appendChild(tag);
      return tag;
    });
    return () => {
      nodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
  }, [JSON.stringify(schemas)]);
};

const SITE_URL = 'https://curveeforyou.com';
const BRAND = {
  '@type': 'Brand',
  name: 'CurveeForYou'
};

export const productSchema = ({
  name,
  description,
  image,           // string or array
  sku,
  price,
  priceCurrency = 'USD',
  url,
  rating,          // number (e.g. 4.9)
  reviewCount,     // number
  availability = 'https://schema.org/InStock'
}) => ({
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name,
  description,
  image: Array.isArray(image) ? image : [image],
  sku: sku || name,
  brand: BRAND,
  offers: {
    '@type': 'Offer',
    url,
    priceCurrency,
    price: price.toFixed(2),
    availability,
    itemCondition: 'https://schema.org/NewCondition',
    seller: { '@type': 'Organization', name: 'CurveeForYou' }
  },
  ...(rating && reviewCount
    ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: rating.toFixed(1),
          reviewCount,
          bestRating: '5',
          worstRating: '1'
        }
      }
    : {})
});

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: it.name,
    item: it.url.startsWith('http') ? it.url : `${SITE_URL}${it.url}`
  }))
});

export const faqPageSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer }
  }))
});
